import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ShieldCheck, Truck, Sprout, Gift, UserRound, HelpCircle } from "lucide-react";
import Seo from "@/components/Seo";

/* ─── FAQ content — categorized for The Farm To You members-only model ─── */
const CATEGORIES = [
  {
    id: "general",
    label: "General",
    icon: HelpCircle,
    items: [
      {
        q: "What exactly is The Farm To You?",
        a: "The Farm To You is a membership-based farm-to-door food platform that connects customers with curated food boxes, limited drops, and specialty products from trusted farms, ranches, restaurants, and food partners across the country. We are not a traditional grocery marketplace. Our model is built around exclusive access, limited releases, and curated boxes designed to bring better food directly to your home.",
      },
      {
        q: "How is The Farm To You different from a regular online grocery store?",
        a: "The Farm To You is built around limited drops and curated food boxes. Instead of offering unlimited inventory like a traditional marketplace, we release select boxes in limited quantities. Some drops may include meats, seafood, recipes, seasonings, sauces, or specialty items from farms, ranches, restaurants, or food partners. Once a drop sells out, it may not come back.",
      },
      {
        q: "Do you support local farmers?",
        a: "Yes. Supporting farmers, ranchers, and food partners is a major part of our mission. We work to highlight the people behind the food and give customers a better connection to where their products come from.",
      },
    ],
  },
  {
    id: "memberships-drops",
    label: "Memberships & Drops",
    icon: Sprout,
    items: [
      {
        q: "Do I have to be a member to buy?",
        a: "Yes. The Farm To You is a membership-based platform. Customers must become a member to access and purchase limited drops, curated boxes, and exclusive farm-to-door offerings.",
      },
      {
        q: "How does membership pricing work?",
        a: "Membership pricing depends on the tier selected. Each tier gives members access to The Farm To You platform, limited drops, and curated boxes. Premium membership tiers may include additional perks such as early access to upcoming drops.",
      },
      {
        q: "What are membership perks?",
        a: "Membership perks may include access to limited drops, exclusive curated boxes, early access depending on membership tier, free shipping on all orders, recipes, cooking inspiration, and special partner releases.",
      },
      {
        q: "What is early access?",
        a: "Premium members receive 48-hour early access to upcoming drops before they open more broadly. Standard members receive access once the drop officially opens. Since boxes are limited in quantity, early access can help members secure boxes before they sell out.",
      },
      {
        q: "What are limited drops?",
        a: "Limited drops are curated food releases available in set quantities. For example, we may release only 500 boxes for a specific drop. These boxes may feature meats, seafood, recipes, seasonings, sauces, restaurant collaborations, ranch partnerships, or specialty items. Once a drop sells out, it may not return.",
      },
      {
        q: "Can I cancel anytime?",
        a: "Yes. Members can cancel their membership anytime through their account portal. Once canceled, you will no longer have access to member-only drops and perks after your current membership period ends.",
      },
      {
        q: "Can I skip a week or pause my subscription?",
        a: "Yes. Members can pause or manage their membership through their account portal. This gives you flexibility if you are traveling, not ready for another order, or want to wait for a future drop.",
      },
    ],
  },
  {
    id: "shipping-delivery",
    label: "Shipping & Delivery",
    icon: Truck,
    items: [
      {
        q: "Do you offer free shipping?",
        a: "Yes. The Farm To You offers free shipping on all orders.",
      },
      {
        q: "Where do you deliver?",
        a: "We currently deliver nationwide across the United States.",
      },
      {
        q: "How does cold-chain shipping work?",
        a: "Orders are packed using cold-chain packaging designed to help protect freshness during transit. Depending on the product and shipping requirements, orders may be shipped with gel packs, dry ice, insulated liners, or other temperature-control packaging.",
      },
      {
        q: "Will my food arrive frozen or refrigerated?",
        a: "Some items may arrive frozen, partially frozen, or chilled depending on the product type, shipping method, and transit time. Meat and seafood boxes are packed to maintain safe temperature standards during delivery.",
      },
      {
        q: "What if something arrives damaged?",
        a: "If your order arrives damaged, please contact our support team as soon as possible with your order number and photos of the issue. We will review the order and work to make it right.",
      },
    ],
  },
  {
    id: "food-quality",
    label: "Food Quality & Freshness",
    icon: ShieldCheck,
    items: [
      {
        q: "How fresh is the food?",
        a: "Our products are sourced through trusted farms, ranches, restaurants, and food partners and packed with care for delivery. Freshness depends on the type of product and drop, but every box is selected with quality, flavor, and proper handling in mind.",
      },
      {
        q: "Where does the food come from?",
        a: "Our food comes from a growing network of farms, ranches, restaurants, and food partners across the country. On our \u201cOur Farms\u201d page, customers can learn more about featured partners, their stories, and the products they help provide.",
      },
      {
        q: "Are your meats responsibly sourced?",
        a: "We aim to work with partners who prioritize quality, responsible sourcing, and better food standards. Specific certifications, sourcing details, and animal welfare claims may vary by farm or supplier and will only be listed when verified.",
      },
      {
        q: "Do your boxes include recipes or seasonings?",
        a: "Some boxes may include recipes, cooking tips, seasonings, sauces, or specialty items depending on the drop. Each product page will explain what is included in that specific box.",
      },
    ],
  },
  {
    id: "gifts-partnerships",
    label: "Gifts & Partnerships",
    icon: Gift,
    items: [
      {
        q: "Do you offer gift memberships?",
        a: "Yes. Gift memberships may be available so customers can share The Farm To You experience with family, friends, clients, or employees.",
      },
      {
        q: "Can I buy a box as a gift?",
        a: "Yes, depending on the drop and availability. Customers may be able to purchase select boxes as gifts and ship them directly to someone else.",
      },
      {
        q: "I run a farm, ranch, restaurant, or food brand. How do I become a partner?",
        a: "We are always interested in connecting with farms, ranches, restaurants, and specialty food brands that align with our mission. Please click the \u201cBecome a Partner\u201d link, fill out the partner inquiry form, and our team will reach out with more information.",
      },
      {
        q: "Do you work with influencers or brand ambassadors?",
        a: "Yes. We partner with creators, athletes, chefs, wellness leaders, pageant queens, personal trainers, and local voices who believe in better food and supporting farmers. Please visit our Brand Partner application page to learn more.",
      },
    ],
  },
  {
    id: "account-privacy",
    label: "Account & Privacy",
    icon: UserRound,
    items: [
      {
        q: "How do I reset my password?",
        a: "You can reset your password by clicking \u201cForgot Password\u201d on the sign-in page. Follow the instructions sent to your email to create a new password.",
      },
      {
        q: "Do you sell my data?",
        a: "No. The Farm To You does not sell customer personal data. Please review our Privacy Policy for more information on how customer information is collected, used, and protected.",
      },
    ],
  },
];

function FaqItem({ q, a, idx, categoryId }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-[var(--ff-border)] last:border-0"
      data-testid={`faq-item-${categoryId}-${idx}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left py-5 flex items-start justify-between gap-6 group"
        data-testid={`faq-question-${categoryId}-${idx}`}
        aria-expanded={open}
      >
        <span className="font-medium text-base text-[var(--ff-text-primary)] group-hover:text-[var(--ff-primary)] transition-colors">
          {q}
        </span>
        <ChevronDown
          size={20}
          className={`shrink-0 mt-0.5 text-[var(--ff-text-muted)] transition-transform ${
            open ? "rotate-180 text-[var(--ff-primary)]" : ""
          }`}
        />
      </button>
      {open && (
        <div
          className="pb-5 pr-10 text-[var(--ff-text-secondary)] leading-relaxed text-[15px]"
          data-testid={`faq-answer-${categoryId}-${idx}`}
        >
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [active, setActive] = useState(CATEGORIES[0].id);

  return (
    <div className="bg-white" data-testid="faq-page">
      <Seo
        title="Frequently Asked Questions"
        description="Memberships, drops, shipping, freshness, partnerships, and account help — answered."
        path="/faq"
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, #FAF7F1 0%, #F3EFE6 70%, #FFFFFF 100%)" }}
      >
        <div className="max-w-5xl mx-auto px-6 py-20 lg:py-24">
          <p className="overline text-[var(--ff-secondary)]">FAQ</p>
          <h1
            className="mt-3 font-display leading-[1.05] text-[var(--ff-text-primary)]"
            style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}
          >
            Questions, answered.
          </h1>
          <p className="mt-5 text-lg text-[var(--ff-text-secondary)] max-w-2xl leading-relaxed">
            How memberships, limited drops, shipping, freshness, and our farm
            partners all fit together — clearly and honestly.
          </p>
        </div>
      </section>

      {/* Category tabs */}
      <section className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-[var(--ff-border)]">
        <div className="max-w-5xl mx-auto px-6 py-3 overflow-x-auto" data-testid="faq-categories">
          <div className="flex gap-2 min-w-max">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const isActive = active === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  data-testid={`faq-cat-${c.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-[var(--ff-primary)] text-white"
                      : "bg-white text-[var(--ff-text-secondary)] border border-[var(--ff-border)] hover:border-[var(--ff-primary)]"
                  }`}
                >
                  <Icon size={14} />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Active category */}
      <section className="max-w-3xl mx-auto px-6 py-14">
        {CATEGORIES.filter((c) => c.id === active).map((cat) => (
          <div key={cat.id} data-testid={`faq-section-${cat.id}`}>
            <div className="flex items-center gap-3 mb-6">
              <cat.icon size={20} className="text-[var(--ff-secondary)]" />
              <h2 className="font-display text-3xl text-[var(--ff-text-primary)]">
                {cat.label}
              </h2>
            </div>
            <div className="bg-white border border-[var(--ff-border)] rounded-2xl px-6">
              {cat.items.map((item, idx) => (
                <FaqItem
                  key={idx}
                  idx={idx}
                  categoryId={cat.id}
                  q={item.q}
                  a={item.a}
                />
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Support CTA */}
      <section
        className="relative py-20 lg:py-24"
        style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FAF7F1 100%)" }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center" data-testid="faq-support-cta">
          <p className="overline text-[var(--ff-secondary)]">Need a hand?</p>
          <h3 className="mt-3 font-display text-3xl lg:text-4xl text-[var(--ff-text-primary)]">
            Still Have Questions?
          </h3>
          <p className="mt-4 text-[var(--ff-text-secondary)] leading-relaxed max-w-2xl mx-auto">
            Our team is here to help with memberships, drops, shipping,
            partnerships, and account support.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/contact"
              data-testid="faq-cta-contact"
              className="btn-primary inline-flex"
            >
              Contact Support
            </Link>
            <Link
              to="/pricing"
              data-testid="faq-cta-member"
              className="btn-secondary inline-flex"
            >
              Become a Member
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
